/**
 * Salesforce Iframe Utilities
 *
 * Utility functions for detecting and managing Salesforce iframe context.
 */

/**
 * Detects if the app is embedded in a Salesforce iframe by checking for required query parameters.
 *
 * @returns true if both 'sobject' and 'id' query parameters are present
 */
export const isSalesforceIframe = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  const sobject = params.get("sobject");
  const id = params.get("id");
  return !!(sobject && id);
};

/**
 * Extracts and serializes Salesforce-specific query parameters.
 *
 * @returns Query string with Salesforce parameters (sobject, id, environment)
 */
export const getSalesforceParams = (): string => {
  const params = new URLSearchParams(window.location.search);
  const sobject = params.get("sobject");
  const id = params.get("id");
  const environment = params.get("environment");

  if (!sobject || !id) return "";

  const salesforceParams = new URLSearchParams({
    sobject,
    id,
    ...(environment && { environment }),
  });

  return salesforceParams.toString();
};

/**
 * Paths that are allowed when the app is embedded in a Salesforce iframe.
 *
 * Note: Uses startsWith matching, so "/pages/integrations/salesforce" allows:
 * - /pages/integrations/salesforce
 * - /pages/integrations/salesforce/:sandbox
 *
 * Note: /pages/profile/integrations/salesforce is intentionally NOT allowed
 */
export const SALESFORCE_ALLOWED_PATHS = [
  "/login",
  "/sign-up",
  "/pages/integrations/salesforce",
] as const;

/**
 * Validates if a given pathname is allowed in Salesforce iframe context.
 *
 * @param pathname - The pathname to validate
 * @returns true if the path is in the allowed list
 */
export const isSalesforceAllowedPath = (pathname: string): boolean => {
  return SALESFORCE_ALLOWED_PATHS.some((allowed) =>
    pathname.startsWith(allowed),
  );
};
